<template v-if="loaded">
	<section class="overview overview--vacancy" ref="results">
		<div class="grid">
			<div class="cell cell--1 cell--medium-2/3">
				<div class="overview__outer">
					<div>
						<button
							class="overview__show-filters-btn btn btn--a btn--has-icon"
							@click="toggleFiltersOverlay()"
						>
							<span class="btn__icon">
								<Icon name="filter" />
							</span>
							<span>Filters</span>
						</button>
						<!-- Posts -->
						<div
							v-if="headers.total < 1 || (!headers.total && !isFetching)"
							:class="classes"
						>
							<div class="heading--3">0 Resultaten</div>
							<div class="card">
								<div v-if="!noResultsText">
									<h2>Probeer je zoekopdracht aan te passen.</h2>
									<p>
										Kom bij ons vrijblijvend een kopje koffie doen, dan kunnen
										we samen kijken of JS Consultancy een geschikte baan heeft
										voor jou!
									</p>
								</div>
								<div v-else>
									<h2 class="heading--4">{{ noResultsTitle }}</h2>
									<p>{{ noResultsText }}</p>
								</div>

								<div class="button-container">
									<button @click="togglePopup" class="btn btn--small btn--a">
										Ga voor mij opzoek
									</button>
									<button
										@click="togglePopupJobAlert"
										class="btn btn--fill-outline btn--small btn--a btn--has-icon"
									>
										<span class="btn__icon">
											<Icon fill="#000" name="alert" />
										</span>
										<span>Job alert instellen</span>
									</button>
								</div>
							</div>
						</div>
						<div v-else :class="classes">
							<div class="heading--3">{{ searchResultsTitle }}</div>
							<template v-for="post in filteredPosts">
								<vacancy-teaser
									:title="post.title.rendered"
									:link="post.link"
									:excerpt="post.excerpt.rendered"
									:salary="post.acf.vacancy_salary"
									:company_logo="post.acf.vacancy_company_logo"
									:location="post.acf.vacancy_city"
									:jobHours="post.acf.vacancy_job_hours"
									:date="post.date"
									:terms="
										post._embedded && post._embedded['wp:term']
											? post._embedded['wp:term']
											: []
									"
								>
								</vacancy-teaser>
							</template>
						</div>
						<!-- LOADING -->
						<div class="overview__actions">
							<div v-if="isFetching">
								<svg
									xmlns=" w3.org xmlns:xlink="
									w3.org
									style="
										margin: auto;
										background: rgb(255, 255, 255);
										display: block;
										shape-rendering: auto;
									"
									width="60px"
									height="60px"
									viewBox="0 0 100 100"
									preserveAspectRatio="xMidYMid"
								>
									<circle
										cx="50"
										cy="50"
										fill="none"
										stroke="#009B96"
										stroke-width="5"
										r="35"
										stroke-dasharray="164.93361431346415 56.97787143782138"
									>
										<animateTransform
											attributeName="transform"
											type="rotate"
											repeatCount="indefinite"
											dur="1s"
											values="0 50 50;360 50 50"
											keyTimes="0;1"
										></animateTransform>
									</circle>
								</svg>
							</div>
							<div
								class="overview__pagination"
								v-if="!isFetching && fetchCompleted && headers['pages'] > 1"
							>
								<a
									href="#"
									data-page="prev"
									@click="setPage"
									:hidden="query['page'] === 1"
									><Icon name="chevron-down" />
									<span class="text">Vorige</span></a
								>
								<div
									v-for="n in pages"
									:key="n"
									:class="{ currentPage: n == query['page'] }"
								>
									<a href="#" :data-page="n" @click="setPage">{{ n }}</a>
								</div>
								<a
									href="#"
									data-page="next"
									@click="setPage"
									:hidden="query['page'] === headers['pages']"
									><span class="text">Volgende</span> <Icon name="chevron-down"
								/></a>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="cell cell--1 cell--medium-1/3 cell--medium-order-start">
				<div class="overview__sidebar overview-filters" v-if="fetchCompleted">
					<div class="overview-filters__inner">
						<div
							class="overview-filters__topbar"
							@click="toggleFiltersOverlay()"
						>
							<Icon name="close" />
						</div>
						<div class="overview-filters__header">
							<div class="overview-filters__title">Verfijn zoekopdracht</div>
							<div class="overview-filters__reset-link">
								<a :href="resetLink()">Reset filters</a>
							</div>
						</div>
						<section class="overview-filters__filters">
							<toggle-content
								name="filter"
								label="Wat is je postcode?"
								:toggled="true"
								type="vacancy"
								icon="chevron"
							>
								<div
									class="overview-filters__item overview-filters__item--text"
								>
									<label class="overview-filters__label" for="zipcode"
										>Postcode</label
									>
									<div class="overview-filters__input-container">
										<input
											class="overview-filters__input-text"
											type="text"
											:value="query.zipcode"
											placeholder="Postcode"
											name="zipcode"
											id="zipcode"
											v-on:keyup.enter="handleZipcodeChange($event)"
										/>
										<button class="overview-filters__input-clear" type="submit">
											<Icon name="search" />
											<span class="visually-hidden">Zoeken</span>
										</button>
									</div>
								</div>
							</toggle-content>

							<toggle-content
								v-for="(category, index) in topTerms"
								:key="category.taxonomy.name"
								v-if="category.taxonomy.name !== 'business_unit'"
								name="filter"
								:label="getCategoryTitle(category.taxonomy)"
								:index="index"
								:toggled="true"
								type="vacancy"
								icon="chevron"
							>
								<div>
									<ul class="overview-filters__list">
										<template
											v-if="
												Array.isArray(category.terms) &&
												category.terms.length <= 5
											"
										>
											<!-- Render all checkboxes if there are 4 or fewer -->
											<li
												v-for="term in category.terms"
												:key="term.term_id"
												v-if="term.parent === 0"
												:class="{
													'overview-filters__item': true,
													'overview-filters__item--checkbox': !hasChildTerms(term)
												}"
											>
												<input
													class="overview-filters__input-checkbox"
													type="checkbox"
													:value="term.term_id"
													:name="category.taxonomy.name"
													v-model.number="activeParams"
													:id="term.slug"
													:checked="term.checked"
													@change="
														handleFilterChange({ event: $event, data: term })
													"
												/>
												<label class="overview-filters__label" :for="term.slug">
													<span v-if="isParentTerm(term)">
														{{ decodeHtmlEntities(term.name) }}
														<!-- {{
															hasChildTerms(term) ? "" : "(" + term.count + ")"
														}} -->
														<toggle-content
															v-if="hasChildTerms(term)"
															name="filter"
															:toggled="false"
															type="vacancy"
															icon="chevron"
															class="toggle__childs"
														>
															<ul v-if="hasChildTerms(term)">
																<li
																	class="overview-filters__item overview-filters__item--checkbox"
																	v-for="childTerm in getChildTerms(term)"
																	:key="childTerm.term_id"
																>
																	<input
																		class="overview-filters__input-checkbox"
																		type="checkbox"
																		:value="childTerm.term_id"
																		:name="category.taxonomy.name"
																		v-model.number="activeParams"
																		:id="childTerm.slug"
																		:checked="childTerm.checked"
																		@change="
																			handleFilterChange({
																				event: $event,
																				data: childTerm,
																			})
																		"
																	/>

																	<label
																		class="overview-filters__label"
																		:for="childTerm.slug"
																	>
																		<span>
																			{{ decodeHtmlEntities(childTerm.name) }}
																			<!-- ({{ childTerm.count || 0 }}) -->
																		</span>
																	</label>
																</li>
															</ul>
														</toggle-content>
													</span>
												</label>
											</li>
										</template>
										<template v-else>
											<!-- Render only first 5 checkboxes if there are more than 5 -->
											<li
												v-for="(term, index) in Object.values(
													category.terms
												).slice(0, 5)"
												:key="term.term_id"
												v-if="term.parent === 0"
												:class="{
													'overview-filters__item': true,
													'overview-filters__item--checkbox': !hasChildTerms(term)
												}"
											>
												<input
													class="overview-filters__input-checkbox"
													type="checkbox"
													:value="term.term_id"
													:name="category.taxonomy.name"
													v-model.number="activeParams"
													:id="term.slug"
													:checked="term.checked"
													@change="
														handleFilterChange({ event: $event, data: term })
													"
												/>
												<label class="overview-filters__label" :for="term.slug">
													<span v-if="isParentTerm(term)">
														{{ decodeHtmlEntities(term.name) }}
														<!-- {{
															hasChildTerms(term) ? "" : "(" + term.count + ")"
														}} -->
														<toggle-content
															v-if="hasChildTerms(term)"
															name="filter"
															:toggled="false"
															type="vacancy"
															icon="chevron"
															class="toggle__childs"
														>
															<ul v-if="hasChildTerms(term)">
																<li
																	class="overview-filters__item overview-filters__item--checkbox"
																	v-for="childTerm in getChildTerms(term)"
																	:key="childTerm.term_id"
																>
																	<input
																		class="overview-filters__input-checkbox"
																		type="checkbox"
																		:value="childTerm.term_id"
																		:name="category.taxonomy.name"
																		v-model.number="activeParams"
																		:id="childTerm.slug"
																		:checked="childTerm.checked"
																		@change="
																			handleFilterChange({
																				event: $event,
																				data: childTerm,
																			})
																		"
																	/>

																	<label
																		class="overview-filters__label"
																		:for="childTerm.slug"
																	>
																		<span>
																			{{ decodeHtmlEntities(childTerm.name) }}
																			<!-- ({{ childTerm.count || 0 }}) -->
																		</span>
																	</label>
																</li>
															</ul>
														</toggle-content>
													</span>
												</label>
											</li>
											<!-- Show button to render the rest -->

											<!-- Hidden checkboxes initially, toggle visibility with 'showMore' method -->
											<li
												v-for="(term, index) in Object.values(
													category.terms
												).slice(5)"
												:key="'hidden_' + index"
												v-if="term.parent === 0"
												v-show="categoryVisibility[category.taxonomy.name]"
												:class="{
													'overview-filters__item': true,
													'overview-filters__item--checkbox': !hasChildTerms(term)
												}"
											>
												<input
													class="overview-filters__input-checkbox"
													type="checkbox"
													:value="term.term_id"
													:name="category.taxonomy.name"
													v-model.number="activeParams"
													:id="term.slug"
													:checked="term.checked"
													@change="
														handleFilterChange({ event: $event, data: term })
													"
												/>
												<label class="overview-filters__label" :for="term.slug">
													<span v-if="isParentTerm(term)">
														{{ decodeHtmlEntities(term.name) }}
														<!-- {{
															hasChildTerms(term) ? "" : "(" + term.count + ")"
														}} -->
														<toggle-content
															v-if="hasChildTerms(term)"
															name="filter"
															:toggled="false"
															type="vacancy"
															icon="chevron"
															class="toggle__childs"
														>
															<ul v-if="hasChildTerms(term)">
																<li
																	class="overview-filters__item overview-filters__item--checkbox"
																	v-for="childTerm in getChildTerms(term)"
																	:key="childTerm.term_id"
																>
																	<input
																		class="overview-filters__input-checkbox"
																		type="checkbox"
																		:value="childTerm.term_id"
																		:name="category.taxonomy.name"
																		v-model.number="activeParams"
																		:id="childTerm.slug"
																		:checked="childTerm.checked"
																		@change="
																			handleFilterChange({
																				event: $event,
																				data: childTerm,
																			})
																		"
																	/>

																	<label
																		class="overview-filters__label"
																		:for="childTerm.slug"
																	>
																		<span>
																			{{ decodeHtmlEntities(childTerm.name) }}
																			<!-- ({{ childTerm.count || 0 }}) -->
																		</span>
																	</label>
																</li>
															</ul>
														</toggle-content>
													</span>
												</label>
											</li>
											<template
												v-if="
													Array.isArray(category.terms) &&
													Object.values(category.terms).length > 4
												"
											>
												<a
													v-if="categoryVisibility[category.taxonomy.name]"
													@click="showMore(category)"
													>Toon minder</a
												>
												<a v-else @click="showMore(category)">Toon meer</a>
											</template>
										</template>
									</ul>
								</div>
							</toggle-content>
						</section>
					</div>
					<button
						class="overview-filters__mobile-cta btn btn--a"
						@click="toggleFiltersOverlay()"
					>
						<span>{{ filterButtonText() }}</span>
					</button>
				</div>
				<div class="overview__sidebar m-top-sm" v-if="fetchCompleted">
					<button
						@click="togglePopupJobAlert"
						class="btn btn--fill-outline btn--small btn--a btn--has-icon"
					>
						<span class="btn__icon">
							<Icon name="alert" />
						</span>
						<span>Job alert instellen</span>
					</button>
				</div>
				<slot v-if="fetchCompleted"></slot>
			</div>
		</div>
	</section>
</template>

<script>
import Icon from "./Icon.vue";
import { store } from "./store.js";
import VacancyTeaser from "./VacancyTeaser.vue";
import ToggleContent from "./ToggleContent.vue";
import axios from "axios";

export default {
	name: "VacancyOverview",
	components: {
		Icon,
		VacancyTeaser,
		ToggleContent: ToggleContent,
	},
	data() {
		return {
			termsApiURL: `${window.SITE_URL}/wp-json/api/v1/terms?cache=0`,
			loaded: false,
			posts: [],
			activeParams: [],
			query: {
				page: 1,
				per_page: 10,
				_embed: 1,
				acf_format: "standard",
			},
			noResultsTitle: "",
			noResultsText: "",
			showAll: false,
			categoryVisibility: {}, // New property to track visibility for each category
			/**
			 * This is a list of 'safe' params to use with the WP REST API query.
			 * Other parameters can create conflicts.
			 *
			 * This array can be extended to include custom params such as taxonomies
			 */
			params: [
				"context",
				"page",
				"per_page",
				"search",
				"after",
				"before",
				"exclude",
				"include",
				"offset",
				"order",
				"orderby",
				"_embed",
				"acf_format",
				"work-province",
				"discipline",
				"weekly-hours",
				"education",
				"work-experience",
				"working-method",
				"contract-type",
				"zipcode",
			],
			history: {
				omit: ["per_page", "_embed", "orderby", "acf_format"],
			},
			headers: {},
			isFetching: false,
			fetchCompleted: false,
			store,
			terms: [],
			topTerms: [],
			// termCounts: [],
			filterOpenClass: "js-state-filter-overlay-open",
			overviewClass: "overview__list",
		};
	},
	computed: {
		filteredPosts() {
			return this.posts;
		},
		classes() {
			return ["overview", this.overviewClass].join(" ");
		},
		pages() {
			let numShown = 5;
			numShown = Math.min(numShown, this.headers.pages);

			let first = this.query.page - Math.floor(numShown / 2);
			first = Math.max(first, 1);
			first = Math.min(first, this.headers.pages - numShown + 1);

			return [...Array(numShown)].map((k, i) => i + first);
		},
		searchResultsTitle() {
			let title = "";

			if (!this.headers.total) {
				title = "";
			} else if (this.headers.total == 1) {
				if (this.query["search"]) {
					title = `1 resultaat voor '${this.query["search"]}'`;
				} else {
					title = `1 resultaat`;
				}
			} else {
				if (this.query["search"]) {
					title = `${this.headers.total} resultaten voor '${this.query["search"]}'`;
				} else {
					title = `${this.headers.total} Resultaten`;
				}
			}

			return title;
		},
	},
	methods: {
		async fetchData(argument) {
			this.posts = [];
			this.loaded = true;
			this.isFetching = true;

			// Order by relevance if a search term exists
			if (this.query["search"] && this.query["search"]?.[0].length !== 0) {
				this.query.orderby = "relevance";
			}

			// Create a safe WP Query using permitted params
			let safeQuery = {};

			Object.keys(this.query).map((p) => {
				if (this.params.includes(p)) safeQuery[p] = this.query[p];
			});

			// Build the url query
			let url = [
				window.SITE_URL,
				this.store.endpoints["vacancy"],
				this.buildUrlQuery(safeQuery),
			].join("");

			try {
				const response = await fetch(url);

				if (response.ok) {
					const data = await response.json();

					// Map headers from response. Headers are used to determine if there are additional pages surrounding a query.
					let headers = {
						total: "X-WP-Total",
						pages: "X-WP-TotalPages",
						link: "Link",
					};

					Object.keys(headers).forEach((key) => {
						const header = response.headers.get(headers[key]);
						const value = isNaN(header) ? header : parseInt(header) || 0;
						headers[key] = value;
					});

					this.headers = headers;
					if (argument !== "skipReplaceState") {
						this.replaceState(safeQuery);
					}
					this.$nextTick(() => {
						this.posts = data;
						this.fetchCompleted = true;
						this.isFetching = false;
					});
				}
			} catch (error) {
				this.handleError(error);
			}
		},
		getState(query = false) {
			query = query ? query : this.buildJsonQuery(window.location.search);
			let params = [];
			Object.keys(query).map((key) => {
				this.$set(this.query, key, query[key]);

				let param = query[key];

				// Get all active filters and store in an array
				if (Array.isArray(param)) {
					let t = param.map((a) => {
						// Exclude the search and pagination parameter
						if (key !== "search" || key !== "page") {
							return a;
						}
					});
					params.push(t);
				}
			});

			// Active terms are grouped by taxonomy, for the activeparams we need to turn this into a flat array
			this.activeParams = params.reduce((a, b) => a.concat(b), []);

			return this;
		},
		replaceState(query) {
			let history = {};
			Object.keys(query).map((p) => {
				// check if the parameter is a filter or search/page
				// for filters, the array is converted to a comma separated string
				if (p === "search" || p === "page") {
					// Exclude the first page from the parameters
					if (!(p === "page" && this.query.page === 1)) {
						history[p] = query[p];
					}
				} else {
					let param = query[p];

					if (Array.isArray(param)) {
						history[p] = param.join(",");
					}
				}
			});

			let state = this.buildUrlQuery(history, this.history.omit);
			window.history.replaceState(null, null, window.location.pathname + state);

			return this;
		},
		getUrlParams(query) {
			return new URLSearchParams(query).toString();
		},
		// Turn the query into a URL Query string
		buildUrlQuery(query, omit = false) {
			let q = Object.keys(query)
				.map((k) => {
					if (omit && omit.includes(k)) return false;
					const parameter = JSON.parse(JSON.stringify(query[k]));

					if (Array.isArray(parameter)) {
						if (parameter.length > 0) {
							const a = parameter.join(",");
							return `${k}=${a}`;
						}
					} else {
						if (query[k]) return `${k}=${query[k]}`;
					}
				})
				.filter((k) => k)
				.join("&");
			return q !== "" ? "?" + q : "";
		},
		// Turn the query into a json object
		buildJsonQuery(query) {
			if (query === "") return false;
			let params = new URLSearchParams(query);
			let q = {};

			// Set keys in object and get values, convert to number (!NaN)
			params.forEach(function (value, key) {
				if (key !== "search") {
					if (!q.hasOwnProperty(key)) {
						let a = value.split(",");
						a = a.map((b) => Number(b));

						q[key] = a;
					}
				} else {
					if (!q.hasOwnProperty(key)) q[key] = value;
				}
			});
			return q;
		},
		async fetchTerms() {
			const rawTerms = await (await fetch(this.termsApiURL)).json();

			// Create a map to easily find terms by their term_id
			const termMap = new Map(rawTerms.map((term) => [term.term_id, term]));

			// Update each term to include its parentTerm
			rawTerms.forEach((term) => {
				if (term.parent && termMap.has(term.parent)) {
					const parentTerm = termMap.get(term.parent);
					if (!parentTerm.terms) {
						parentTerm.terms = [];
					}
					parentTerm.terms.push(term);
					term.parentTerm = parentTerm;
				}
			});

			// Filter out the top-level terms that have no parents
			// const topLevelTerms = rawTerms.filter((term) => !term.parent);
			const topLevelTerms = rawTerms.map((obj) => ({
				taxonomy: obj.taxonomy,
				terms: obj.terms.filter((term) => term.parent === 0),
			}));

			this.terms = rawTerms;
			this.topTerms = topLevelTerms;

			// this.terms = await (await fetch(this.termsApiURL)).json();
			//         // Initialize term counts based on the fetched data
			//         this.terms.forEach(category => {
			//             if (Array.isArray(category.terms)) {
			//                 category.terms.forEach(term => {
			//                     this.$set(this.termCounts, term.term_id, term.count || 0);
			//                 });
			//             }
			//         });
		},
		handleError(response) {
			// eslint-disable-next-line no-undef
			if (process.env.NODE_ENV === "development") {
				console.dir(response);
			}

			this.isFetching = false;
		},
		handleFilterChange(event) {
			const taxonomy = event.data.taxonomy;
			const term = Number(event.data.term_id);

			const parentCheckbox = document.getElementById(event.data.slug);
			const eventChecked = parentCheckbox.checked;

			const checkbox = document.getElementById("p_" + event.data.slug);
			if (checkbox) {
				checkbox.checked = eventChecked;
			}

			let terms = this.query.hasOwnProperty(taxonomy)
				? this.query[taxonomy].slice() // Copy the array to avoid mutating the original query
				: []; // Initialize as an empty array if not present

			// Toggle: If the term is already in the query, remove it; otherwise, add it.
			if (terms.includes(term)) {
				terms = terms.filter((e) => e !== term);
			} else {
				terms.push(term);
			}

			if (this.hasChildTerms(event.data)) {
				const child = this.getChildTerms(event.data);

				child.forEach((term) => {
					const childCheckbox = document.getElementById(term.slug);
					const childTerm = Number(term.term_id);

					const childCheckboxForm = document.getElementById("p_" + term.slug);
					if (childCheckboxForm) {
						childCheckboxForm.checked = eventChecked;
					}

					const jobAlertCheckbox = document.getElementById("p_" + term.slug);
					if (jobAlertCheckbox) {
						jobAlertCheckbox.checked = eventChecked;
					}

					if (childCheckbox.checked !== eventChecked) {
						childCheckbox.checked = eventChecked;

						// Toggle: If the childTerm is already in the query, remove it; otherwise, add it.
						if (terms.includes(childTerm)) {
							terms = terms.filter((e) => e !== childTerm);
						} else {
							terms.push(childTerm);
						}
					}
				});
			}

			this.updateQueryToUrl(taxonomy, terms);
			return this;
		},
		debounce(func, delay) {
			let timeoutId;
			return function (...args) {
				clearTimeout(timeoutId);
				timeoutId = setTimeout(() => {
					func.apply(this, args);
				}, delay);
			};
		},
		handleZipcodeChange(event) {
			const userZipcode = event.target.value;

			// TODO: logic for zipcode change here
			this.updateQuery("zipcode", userZipcode);

			return this;
		},
		handleZipcodeClear(event) {
			this.updateQuery("zipcode", null);

			return this;
		},
		updateQuery(taxonomy, terms) {
			this.query[taxonomy] = terms;
			this.query["page"] = 1;

			this.fetchData();
		},
		updateQueryToUrl(taxonomy, terms) {
			this.query[taxonomy] = terms;
			this.query["page"] = 1;

			let safeQuery = {};

			Object.keys(this.query).map((p) => {
				if (this.params.includes(p)) safeQuery[p] = this.query[p];
			});
			this.replaceState(safeQuery);

			this.debouncedFetchData("skipReplaceState");
		},
		setPage(event) {
			event.preventDefault();

			const nextPage = event.target.dataset.page;
			let page = this.query["page"];

			if (nextPage === "prev") {
				page--;
			} else if (nextPage === "next") {
				page++;
			} else {
				page = Number(nextPage);
			}

			this.query["page"] = page;

			this.scrollToTop();

			this.fetchData();
		},
		getCategoryTitle(taxonomy) {
			if (this.store.filterLabels.hasOwnProperty(taxonomy.name)) {
				return this.store.filterLabels[taxonomy.name];
			} else {
				return taxonomy.label;
			}
		},
		getCompanyLogo(image) {
			return image;
		},
		decodeHtmlEntities(text) {
			return text.replace(/&amp;/g, "&");
		},
		resetLink() {
			let url = [window.SITE_URL, window.location.pathname].join("");

			return url;
		},
		scrollToTop() {
			const element = this.$refs["results"];
			const top = element.offsetTop;

			window.scrollTo({ left: 0, top: top, behavior: "instant" });
		},
		toggleFiltersOverlay() {
			document.documentElement.classList.toggle(this.filterOpenClass);
		},
		filterButtonText() {
			return `Toon resultaten (${this.headers.total})`;
		},
		togglePopup() {
			const form = document.getElementById("no-results-form");
			if (!form) {
				console.log("No form found");
				return;
			}
			if (form.style.display === "none") {
				form.style.display = "block";
				form.style.visibility = "visible";
			} else {
				form.style.display = "none";
				form.style.visibility = "hidden";
			}
		},
		togglePopupJobAlert() {
			const form = document.getElementById("job-alert-form");
			if (!form) {
				console.log("No form found");
				return;
			}
			if (form.style.display === "none") {
				form.style.display = "block";
				form.style.visibility = "visible";
			} else {
				form.style.display = "none";
				form.style.visibility = "hidden";
			}

			const sourceInput = document.getElementById("zipcode");
			const targetInput = document.getElementById("input_postcode");
			if (sourceInput && targetInput) {
				targetInput.value = sourceInput.value;
			}
		},
		showMore(category) {
			this.$set(
				this.categoryVisibility,
				category.taxonomy.name,
				!this.categoryVisibility[category.taxonomy.name]
			);
		},
		// Function to check if a term is a parent
		isParentTerm(term) {
			return term.parent === 0; // Assuming 0 indicates a top-level parent
		},
		// Function to check if a parent term has child terms
		getChildTerms(parentTerm) {
			const childTerms = [];

			for (const category of this.terms) {
				if (!Array.isArray(category.terms)) {
					continue;
				}

				if (category.terms.length < 1) {
					// skip if empty
					continue;
				}

				const firstTerm = category.terms[0];

				if (firstTerm["taxonomy"] !== parentTerm.taxonomy) {
					continue;
				}

				const filterTerm = category.terms.filter(
					(term) => term.parent === parentTerm.term_id
				);
				if (filterTerm.length > 0) {
					childTerms.push(...filterTerm);
				}
			}

			return childTerms;

			// return this.terms.filter((term) => term.parent === parentTerm.term_id);
		},
		hasChildTerms(parentTerm) {
			return this.getChildTerms(parentTerm).length > 0;
		},
		getParentName(term) {
			if (term.parent === 0) {
				// Term is already a parent
				return term.name;
			} else {
				// Find the parent term by ID
				const parentTerm = this.findTermById(term.parent);
				return parentTerm ? parentTerm.name : "";
			}
		},
		findTermById(termId) {
			// Loop through all terms and find the one with the matching ID
			for (const category of this.terms) {
				if (!Array.isArray(category.terms)) {
					continue;
				}

				const foundTerm = category.terms.find(
					(term) => term.term_id === termId
				);
				if (foundTerm) {
					return foundTerm;
				}
			}
			return null; // Return null if no matching term is found
		},
		// getChildTermsCount(parentTerm) {
		// 	return this.getChildTerms(parentTerm).length;
		// },
	},
	props: ["noresults", "noresultstext"],
	mounted: function () {
		if (this.noresults) {
			this.noResultsTitle = this.noresults;
		}
		if (this.noresultstext) {
			this.noResultsText = this.noresultstext;
		}
		this.getState();
		this.fetchData();
		this.fetchTerms();
		this.debouncedFetchData = this.debounce(this.fetchData.bind(this), 300);

		const externalForm = document.getElementById("gform_job_alert");

		if (externalForm) {
			const submitButton = externalForm.querySelector('[type="submit"]');

			submitButton.addEventListener("click", (event) => {
				event.preventDefault();

				const consentCheckbox = externalForm.querySelector(
					'[name="input_consent_1"]'
				);
				if (consentCheckbox && !consentCheckbox.checked) {
					alert("Please fill all the required fields.");
					return;
				}

				const formData = new FormData(externalForm);

				axios
					.post("/wp-json/vacancy/v1/axiosJobAlert", formData)
					.then((response) => {
						console.log("Form submitted successfully", response.data);

						if (response.data.error) {
							alert(response.data.message);
						} else {
							alert('Je job alert is aangemaakt, dankjewel voor je aanmelding.');
							this.togglePopupJobAlert();
							history.replaceState(null, '', window.location.pathname);
							window.location.reload();
						}
					})
					.catch((error) => {
						console.error("Form submission failed", error);
					});
			});
		}
	},
};
</script>

<style scoped></style>
